import React, { useState, useEffect, useCallback } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Code, Loader2, Play } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Badge } from '../../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select';
import { lambdaService } from '../../../services/lambdaService';
import { useUpstreamFile } from '../useUpstreamFile';

type QueryMode = 'polars' | 'ai';

interface ModelOption {
  value: string;
  label: string;
  provider: 'gpt-5' | 'claude' | 'bedrock';
  claudeModel?: string;
  bedrockModel?: string;
  gpt5Reasoning?: string;
}

const MODEL_OPTIONS: ModelOption[] = [
  { value: 'gpt5-minimal', label: 'GPT-5 (minimal)', provider: 'gpt-5', gpt5Reasoning: 'minimal' },
  { value: 'gpt5-low', label: 'GPT-5 (low)', provider: 'gpt-5', gpt5Reasoning: 'low' },
  { value: 'gpt5-medium', label: 'GPT-5 (medium)', provider: 'gpt-5', gpt5Reasoning: 'medium' },
  { value: 'gpt5-high', label: 'GPT-5 (high)', provider: 'gpt-5', gpt5Reasoning: 'high' },
  { value: 'claude-haiku', label: 'Claude 4.5 Haiku', provider: 'claude', claudeModel: 'claude-haiku-4-5' },
  { value: 'claude-sonnet', label: 'Claude 3.5 Sonnet', provider: 'claude', claudeModel: 'claude-3-5-sonnet-20241022' },
  { value: 'claude-opus', label: 'Claude 3 Opus', provider: 'claude', claudeModel: 'claude-3-opus-20240229' },
  { value: 'bedrock-120b', label: 'Secure GPT (120B)', provider: 'bedrock', bedrockModel: 'openai.gpt-oss-120b-1:0' },
  { value: 'bedrock-20b', label: 'Secure GPT (20B)', provider: 'bedrock', bedrockModel: 'openai.gpt-oss-20b-1:0' },
];

export const QueryNode: React.FC<{ id: string; data: { label: string; color: string; isTemplate?: boolean } }> = ({ id, data }) => {
  const selectedFile = useUpstreamFile(id);
  const [mode, setMode] = useState<QueryMode>('polars');
  const [polarsQuery, setPolarsQuery] = useState('result = df.lazy().head(10).collect()');
  const [aiPrompt, setAiPrompt] = useState('');
  const [selectedModel, setSelectedModel] = useState('gpt5-minimal');
  const [generatedCode, setGeneratedCode] = useState('');
  const [results, setResults] = useState<any[]>([]);
  const [resultColumns, setResultColumns] = useState<string[]>([]);
  const [running, setRunning] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [execTime, setExecTime] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [headers, setHeaders] = useState<string[]>([]);
  const [sampleRows, setSampleRows] = useState<any[][]>([]);

  const fileName = selectedFile?.fileName || '';

  // Fetch schema sample for AI mode
  useEffect(() => {
    if (!selectedFile) return;
    lambdaService
      .runAnalysis(fileName, 'result = df.lazy().head(3).collect()', 1)
      .then((res) => {
        if (res && res.result && res.result.length > 0) {
          const cols = Object.keys(res.result[0]);
          setHeaders(cols);
          setSampleRows(res.result.slice(0, 3).map((row: any) => cols.map((c) => row[c])));
        }
      })
      .catch(() => {});
  }, [selectedFile, fileName]);

  const runPolarsQuery = useCallback(async (query: string) => {
    if (!fileName || !query.trim()) return;
    setRunning(true);
    setError('');
    try {
      const res = await lambdaService.runAnalysis(fileName, query, 5);
      if (res && res.result && res.result.length > 0) {
        setResults(res.result.slice(0, 3));
        setResultColumns(Object.keys(res.result[0]).slice(0, 3));
        setExecTime(res.executionTime || null);
      } else if (res) {
        setResults([]);
        setResultColumns([]);
        setExecTime(res.executionTime || null);
      } else {
        setError('Query failed');
      }
    } catch (e: any) {
      setError(e.message || 'Query failed');
    } finally {
      setRunning(false);
    }
  }, [fileName]);

  const handleRunPolars = () => runPolarsQuery(polarsQuery);

  const handleGenerate = async () => {
    if (!aiPrompt.trim() || !fileName) return;
    setGenerating(true);
    setError('');
    setGeneratedCode('');
    try {
      const modelOption = MODEL_OPTIONS.find((m) => m.value === selectedModel)!;
      const res = await lambdaService.generateQuery(
        aiPrompt,
        headers,
        sampleRows,
        fileName,
        {
          provider: modelOption.provider,
          claudeModel: modelOption.claudeModel as any,
          bedrockModel: modelOption.bedrockModel as any,
          gpt5ReasoningEffort: modelOption.gpt5Reasoning as any,
        }
      );
      if (res.success && res.query) {
        setGeneratedCode(res.query);
        // Auto-run the generated query
        await runPolarsQuery(res.query);
      } else {
        setError(res.error || 'Generation failed');
      }
    } catch (e: any) {
      setError(e.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <div className="bg-card border rounded-xl shadow-md w-[400px] overflow-hidden">
      <div
        className="px-3 py-2 text-xs font-semibold text-white flex items-center gap-1.5"
        style={{ background: data.color }}
      >
        <Code className="h-3.5 w-3.5" />
        {data.label}
      </div>

      <div className="p-3 space-y-2">
        {!selectedFile ? (
          <p className="text-[11px] text-muted-foreground text-center py-2">
            Connect a data source
          </p>
        ) : (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground bg-muted/50 rounded-md px-2 py-1">
            <span className="truncate">{selectedFile.fileName}</span>
          </div>
        )}

        {selectedFile && (
          <>
            {/* Mode toggle */}
            <div className="flex items-center gap-2 nodrag">
              <label className="flex items-center gap-1 text-[11px] cursor-pointer">
                <input
                  type="radio"
                  name="query-mode"
                  checked={mode === 'polars'}
                  onChange={() => setMode('polars')}
                  className="h-3 w-3"
                />
                Write Polars
              </label>
              <label className="flex items-center gap-1 text-[11px] cursor-pointer">
                <input
                  type="radio"
                  name="query-mode"
                  checked={mode === 'ai'}
                  onChange={() => setMode('ai')}
                  className="h-3 w-3"
                />
                AI Generate
              </label>
            </div>

            {/* Polars mode */}
            {mode === 'polars' && (
              <>
                <div className="nodrag nowheel">
                  <textarea
                    className="w-full h-[60px] text-[11px] font-mono bg-muted rounded p-2 border-0 resize-none focus:outline-none focus:ring-1 focus:ring-primary"
                    value={polarsQuery}
                    onChange={(e) => setPolarsQuery(e.target.value)}
                    placeholder="result = df.lazy().head(10).collect()"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    className="text-xs h-7"
                    onClick={handleRunPolars}
                    disabled={running || !polarsQuery.trim()}
                  >
                    {running ? (
                      <Loader2 className="h-3 w-3 animate-spin mr-1" />
                    ) : (
                      <Play className="h-3 w-3 mr-1" />
                    )}
                    Run Query
                  </Button>
                  {execTime !== null && (
                    <Badge variant="outline" className="text-[10px]">
                      {execTime}ms
                    </Badge>
                  )}
                </div>
              </>
            )}

            {/* AI mode */}
            {mode === 'ai' && (
              <>
                <div className="nodrag">
                  <Select value={selectedModel} onValueChange={setSelectedModel}>
                    <SelectTrigger className="h-7 text-[11px]">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {MODEL_OPTIONS.map((m) => (
                        <SelectItem key={m.value} value={m.value} className="text-xs">
                          {m.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="nodrag">
                  <Input
                    className="h-7 text-[11px]"
                    placeholder="Show me top 10 customers by revenue"
                    value={aiPrompt}
                    onChange={(e) => setAiPrompt(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleGenerate()}
                  />
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  className="w-full text-xs h-7"
                  onClick={handleGenerate}
                  disabled={generating || !aiPrompt.trim()}
                >
                  {generating ? (
                    <Loader2 className="h-3 w-3 animate-spin mr-1" />
                  ) : (
                    <Play className="h-3 w-3 mr-1" />
                  )}
                  Generate & Run
                </Button>
                {generatedCode && (
                  <div className="nodrag nowheel">
                    <pre className="text-[10px] font-mono bg-muted rounded p-2 overflow-x-auto whitespace-pre-wrap max-h-[60px]">
                      {generatedCode}
                    </pre>
                    <Badge variant="secondary" className="text-[9px] mt-1">
                      Generated by {MODEL_OPTIONS.find((m) => m.value === selectedModel)?.label}
                    </Badge>
                  </div>
                )}
              </>
            )}

            {/* Results table */}
            {results.length > 0 && (
              <div className="overflow-auto max-h-[100px] border rounded text-[11px] nodrag nowheel">
                <table className="w-full">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      {resultColumns.map((c) => (
                        <th key={c} className="text-left px-2 py-1 font-medium whitespace-nowrap">
                          {c}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((row, i) => (
                      <tr key={i} className="border-t">
                        {resultColumns.map((c) => (
                          <td key={c} className="px-2 py-1 truncate max-w-[100px]">
                            {row[c] != null ? String(row[c]) : ''}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Error */}
            {error && (
              <p className="text-xs text-destructive">{error}</p>
            )}
          </>
        )}
      </div>

      <Handle type="target" position={Position.Left} className="!bg-muted-foreground" />
      <Handle type="source" position={Position.Right} className="!bg-muted-foreground" />
    </div>
  );
};
